// Manages command prefill state and builders used to seed the command entry input.
import { useCallback, useRef, useState } from "react";

export interface CommandPrefillRequest {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  requestId: number;
}

interface UseSheetCommandPrefillParams {
  formatAmount: (value: number) => string;
  formatSignedAmount: (value: number) => string;
  documentTitle: string;
}

function formatAccountForCommand(accountName: string): string {
  if (/^[a-z0-9]+$/i.test(accountName)) {
    return accountName;
  }

  const escapedAccountName = accountName.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
  return `"${escapedAccountName}"`;
}

export default function useSheetCommandPrefill({
  formatAmount,
  formatSignedAmount,
  documentTitle,
}: UseSheetCommandPrefillParams) {
  const [commandPrefillRequest, setCommandPrefillRequest] = useState<CommandPrefillRequest | null>(null);
  const nextCommandPrefillIdRef = useRef(1);

  const setAndIncrementPrefillRequest = useCallback((request: Omit<CommandPrefillRequest, "requestId">) => {
    setCommandPrefillRequest({
      ...request,
      requestId: nextCommandPrefillIdRef.current,
    });
    nextCommandPrefillIdRef.current += 1;
  }, []);

  const prefillCommand = useCallback((value: string) => {
    setAndIncrementPrefillRequest({
      value,
      selectionStart: value.length,
      selectionEnd: value.length,
    });
  }, [setAndIncrementPrefillRequest]);

  const handleAmountEditRequest = useCallback((reference: string, signedAmount: number) => {
    const amountText = formatAmount(Math.abs(signedAmount));
    const prefix = `edit ${reference} `;

    setAndIncrementPrefillRequest({
      value: `${prefix}${amountText}`,
      selectionStart: prefix.length,
      selectionEnd: prefix.length + amountText.length,
    });
  }, [formatAmount, setAndIncrementPrefillRequest]);

  const handleCommentEditRequest = useCallback((reference: string, signedAmount: number, commentText: string) => {
    const amountText = formatSignedAmount(signedAmount);
    const escapedComment = commentText.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
    const prefix = `edit ${reference} ${amountText} "`;

    setAndIncrementPrefillRequest({
      value: `${prefix}${escapedComment}"`,
      selectionStart: prefix.length,
      selectionEnd: prefix.length + escapedComment.length,
    });
  }, [formatSignedAmount, setAndIncrementPrefillRequest]);

  const handleBeginningBalanceEditRequest = useCallback((accountName: string, signedAmount: number) => {
    const amountText = formatSignedAmount(signedAmount);
    const accountToken = formatAccountForCommand(accountName);
    const prefix = `bbal ${accountToken} `;

    setAndIncrementPrefillRequest({
      value: `${prefix}${amountText}`,
      selectionStart: prefix.length,
      selectionEnd: prefix.length + amountText.length,
    });
  }, [formatSignedAmount, setAndIncrementPrefillRequest]);

  const handleTopLevelCommentRequest = useCallback((reference: string, commentText: string) => {
    const escapedComment = commentText.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
    const prefix = `comment ${reference} "`;

    setAndIncrementPrefillRequest({
      value: `${prefix}${escapedComment}"`,
      selectionStart: prefix.length,
      selectionEnd: prefix.length + escapedComment.length,
    });
  }, [setAndIncrementPrefillRequest]);

  const handleNewCommentRequest = useCallback((reference: string) => {
    const prefix = `comment ${reference} "`;

    setAndIncrementPrefillRequest({
      value: `${prefix}"`,
      selectionStart: prefix.length,
      selectionEnd: prefix.length,
    });
  }, [setAndIncrementPrefillRequest]);

  const handleTitleEditRequest = useCallback(() => {
    const hasTitle = documentTitle.trim().length > 0;
    const escapedTitle = documentTitle.replace(/\\/g, "\\\\").replace(/"/g, "\\\"");
    const prefix = "title \"";

    setAndIncrementPrefillRequest({
      value: hasTitle ? `${prefix}${escapedTitle}"` : `${prefix}"`,
      selectionStart: prefix.length,
      selectionEnd: hasTitle ? prefix.length + escapedTitle.length : prefix.length,
    });
  }, [documentTitle, setAndIncrementPrefillRequest]);

  return {
    commandPrefillRequest,
    prefillCommand,
    handleAmountEditRequest,
    handleCommentEditRequest,
    handleBeginningBalanceEditRequest,
    handleTopLevelCommentRequest,
    handleNewCommentRequest,
    handleTitleEditRequest,
  };
}
